import { useState, useEffect } from "react";
import { clientsAPI, appointmentsAPI } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Search, Phone, Mail, Calendar, Trash2, Edit, User, Download } from "lucide-react";
import { toast } from "sonner";
import { formatDate } from "@/lib/utils";

export default function ClientsPage() {
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [historySearchTerm, setHistorySearchTerm] = useState(""); // NEW: Search for appointment history
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [showDetailDialog, setShowDetailDialog] = useState(false);
    const [selectedClient, setSelectedClient] = useState(null);
    const [clientHistory, setClientHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [formData, setFormData] = useState({ name: "", phone: "", email: "", notes: "" });
    const [isEditing, setIsEditing] = useState(false);

    useEffect(() => {
        loadClients();
    }, []);

    const loadClients = async () => {
        try {
            const response = await clientsAPI.getAll();
            const clientsData = response.data?.clients || response.data?.data || [];
            setClients(Array.isArray(clientsData) ? clientsData : []);
        } catch (error) {
            toast.error("Failed to load clients");
            setClients([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        if (!formData.name || !formData.phone) {
            toast.error("Name and phone are required");
            return;
        }
        try {
            if (isEditing && selectedClient) {
                await clientsAPI.update(selectedClient.id, formData);
                toast.success("Client updated!");
            } else {
                await clientsAPI.create(formData);
                toast.success("Client added!");
            }
            setShowAddDialog(false);
            setFormData({ name: "", phone: "", email: "", notes: "" });
            setIsEditing(false);
            setSelectedClient(null);
            loadClients();
        } catch (error) {
            const errorMsg = error.response?.data?.error || error.response?.data?.message || "Failed to save client";
            toast.error(typeof errorMsg === 'string' ? errorMsg : "Failed to save client");
        }
    };

    const handleEdit = (client) => {
        setSelectedClient(client);
        setFormData({
            name: client.name,
            phone: client.phone,
            email: client.email || "",
            notes: client.notes || "",
        });
        setIsEditing(true);
        setShowAddDialog(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this client?")) return;
        try {
            await clientsAPI.delete(id);
            toast.success("Client deleted");
            loadClients();
        } catch (error) {
            toast.error("Failed to delete client");
        }
    };

    const viewClientHistory = async (client) => {
        setSelectedClient(client);
        setShowDetailDialog(true);
        setLoadingHistory(true);
        setClientHistory([]);
        setHistorySearchTerm(""); // Reset search when opening new client
        
        try {
            const response = await appointmentsAPI.getAll();
            const appointmentsData = response.data?.appointments || response.data?.data || [];
            
            const history = appointmentsData.filter(appt => {
                if (appt.client_id) {
                    if (String(appt.client_id).toLowerCase() === String(client.id).toLowerCase()) return true;
                }
                if (appt.client?.id) {
                    if (String(appt.client.id).toLowerCase() === String(client.id).toLowerCase()) return true;
                }
                if (appt.client_name && client.name) {
                    if (appt.client_name.toLowerCase().trim() === client.name.toLowerCase().trim()) return true;
                }
                if (appt.client?.name && client.name) {
                    if (appt.client.name.toLowerCase().trim() === client.name.toLowerCase().trim()) return true;
                }
                return false;
            });
            
            history.sort((a, b) => new Date(b.date) - new Date(a.date));
            setClientHistory(history);
        } catch (error) {
            toast.error("Failed to load appointment history");
            setClientHistory([]);
        } finally {
            setLoadingHistory(false);
        }
    };

    const exportClients = () => {
        if (clients.length === 0) {
            toast.error("No clients to export");
            return;
        }

        const csv = [
            ['Name', 'Phone', 'Email', 'Visits', 'Last Visit', 'Notes'],
            ...clients.map(c => [
                c.name,
                c.phone,
                c.email || '',
                c.visit_count || 0,
                c.last_visit || 'Never',
                (c.notes || '').replace(/,/g, ';')
            ])
        ].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');

        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `clients-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Clients exported!");
    };

    const filteredClients = clients.filter(c =>
        c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.phone?.includes(searchTerm)
    );

    // NEW: Filter appointment history based on search
    const filteredHistory = clientHistory.filter(appt => {
        const searchLower = historySearchTerm.toLowerCase();
        const serviceName = appt.service_name || appt.service?.name || '';
        const status = appt.status || '';
        const date = appt.date || '';
        
        return serviceName.toLowerCase().includes(searchLower) ||
               status.toLowerCase().includes(searchLower) ||
               date.includes(searchLower);
    });

    const resetAddDialog = () => {
        setIsEditing(false);
        setSelectedClient(null);
        setFormData({ name: "", phone: "", email: "", notes: "" });
        setShowAddDialog(true);
    };

    return (
        <div className="p-4 md:p-8 lg:p-12 space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="font-heading text-3xl font-bold">Clients</h1>
                    <p className="text-muted-foreground">{clients.length} total clients</p>
                </div>
                <div className="flex items-center gap-3">
                    <Button variant="outline" onClick={exportClients} disabled={clients.length === 0}>
                        <Download className="w-4 h-4 mr-2" />
                        Export
                    </Button>
                    <Button onClick={resetAddDialog} className="rounded-full">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Client
                    </Button>
                </div>
            </div>

            {/* Search */}
            <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                    placeholder="Search clients..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                />
            </div>

            {/* Clients Grid */}
            {loading ? (
                <div className="flex items-center justify-center h-[300px]">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
            ) : filteredClients.length === 0 ? (
                <Card className="border-border/40 shadow-card">
                    <CardContent className="text-center py-12">
                        <User className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                        <p className="text-muted-foreground">
                            {searchTerm ? "No clients found matching your search" : "No clients found"}
                        </p>
                        <Button variant="link" onClick={resetAddDialog}>
                            Add your first client
                        </Button>
                    </CardContent>
                </Card>
            ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredClients.map((client) => (
                        <Card 
                            key={client.id} 
                            className="border-border/40 shadow-card hover:shadow-card-hover transition-shadow cursor-pointer"
                            onClick={() => viewClientHistory(client)}
                        >
                            <CardContent className="p-6">
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-12 h-12 rounded-full bg-accent flex items-center justify-center">
                                            <span className="text-lg font-semibold text-accent-foreground">
                                                {client.name?.charAt(0).toUpperCase() || '?'}
                                            </span>
                                        </div>
                                        <div>
                                            <h3 className="font-semibold">{client.name}</h3>
                                            <p className="text-sm text-muted-foreground">
                                                {client.visit_count || 0} visits
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                                        <Button 
                                            size="icon" 
                                            variant="ghost" 
                                            className="h-8 w-8"
                                            onClick={() => handleEdit(client)}
                                        >
                                            <Edit className="w-4 h-4" />
                                        </Button>
                                        <Button 
                                            size="icon" 
                                            variant="ghost" 
                                            className="h-8 w-8 text-destructive"
                                            onClick={() => handleDelete(client.id)}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                                <div className="space-y-2 text-sm">
                                    <div className="flex items-center gap-2 text-muted-foreground">
                                        <Phone className="w-4 h-4" />
                                        {client.phone}
                                    </div>
                                    {client.email && (
                                        <div className="flex items-center gap-2 text-muted-foreground">
                                            <Mail className="w-4 h-4" />
                                            {client.email}
                                        </div>
                                    )}
                                    {client.last_visit && (
                                        <div className="flex items-center gap-2 text-muted-foreground">
                                            <Calendar className="w-4 h-4" />
                                            Last visit: {formatDate(client.last_visit)}
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* Add/Edit Client Dialog */}
            <Dialog open={showAddDialog} onOpenChange={(open) => {
                setShowAddDialog(open);
                if (!open) {
                    setIsEditing(false);
                    setSelectedClient(null);
                    setFormData({ name: "", phone: "", email: "", notes: "" });
                }
            }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-heading">
                            {isEditing ? "Edit Client" : "Add New Client"}
                        </DialogTitle>
                        <DialogDescription>Enter client details below</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label>Name *</Label>
                            <Input
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder="Client name"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Phone *</Label>
                            <Input
                                value={formData.phone}
                                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                placeholder="+233 20 000 0000"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Email (optional)</Label>
                            <Input
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                placeholder="email@example.com"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Notes (optional)</Label>
                            <Textarea
                                value={formData.notes}
                                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                placeholder="Any special notes..."
                                rows={3}
                            />
                        </div>
                        <div className="flex justify-end gap-2 pt-4">
                            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                                Cancel
                            </Button>
                            <Button onClick={handleSubmit}>
                                {isEditing ? "Update" : "Add"} Client
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Client Detail Dialog */}
            <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="font-heading">{selectedClient?.name}</DialogTitle>
                        <DialogDescription>Client history and details</DialogDescription>
                    </DialogHeader>
                    {selectedClient && (
                        <div className="space-y-6">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="p-4 rounded-xl bg-secondary/50">
                                    <p className="text-sm text-muted-foreground">Total Visits</p>
                                    <p className="text-2xl font-bold">{selectedClient.visit_count || 0}</p>
                                </div>
                                <div className="p-4 rounded-xl bg-secondary/50">
                                    <p className="text-sm text-muted-foreground">Last Visit</p>
                                    <p className="text-lg font-medium">
                                        {selectedClient.last_visit ? formatDate(selectedClient.last_visit) : 'Never'}
                                    </p>
                                </div>
                            </div>

                            {/* Contact Information with clickable links */}
                            <div className="p-4 rounded-xl bg-secondary/50">
                                <p className="text-sm text-muted-foreground mb-2">Contact Information</p>
                                <div className="space-y-2">
                                    <div className="flex items-center gap-2 text-sm">
                                        <Phone className="w-4 h-4 flex-shrink-0" />
                                        <a 
                                            href={`tel:${selectedClient.phone}`}
                                            className="text-primary hover:underline"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            {selectedClient.phone}
                                        </a>
                                    </div>
                                    {selectedClient.email && (
                                        <div className="flex items-center gap-2 text-sm">
                                            <Mail className="w-4 h-4 flex-shrink-0" />
                                            <a 
                                                href={`mailto:${selectedClient.email}`}
                                                className="text-primary hover:underline"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                {selectedClient.email}
                                            </a>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {selectedClient.notes && (
                                <div className="p-4 rounded-xl bg-secondary/50">
                                    <p className="text-sm text-muted-foreground mb-1">Notes</p>
                                    <p className="text-sm">{selectedClient.notes}</p>
                                </div>
                            )}

                            {/* Appointment History with Search */}
                            <div>
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="font-heading font-semibold">Appointment History</h3>
                                    {clientHistory.length > 0 && (
                                        <span className="text-sm text-muted-foreground">
                                            {filteredHistory.length} of {clientHistory.length} appointments
                                        </span>
                                    )}
                                </div>
                                
                                {/* Search Box for Appointment History */}
                                {clientHistory.length > 0 && (
                                    <div className="relative mb-3">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                        <Input
                                            placeholder="Search appointments by service, status, or date..."
                                            value={historySearchTerm}
                                            onChange={(e) => setHistorySearchTerm(e.target.value)}
                                            className="pl-10"
                                        />
                                    </div>
                                )}

                                {loadingHistory ? (
                                    <div className="flex items-center justify-center py-8">
                                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                                    </div>
                                ) : clientHistory.length === 0 ? (
                                    <div className="text-center py-8 bg-secondary/30 rounded-xl">
                                        <Calendar className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
                                        <p className="text-muted-foreground">No appointments yet</p>
                                    </div>
                                ) : filteredHistory.length === 0 ? (
                                    <div className="text-center py-8 bg-secondary/30 rounded-xl">
                                        <Search className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
                                        <p className="text-muted-foreground">No appointments match your search</p>
                                    </div>
                                ) : (
                                    <div className="max-h-[300px] overflow-y-auto border rounded-lg">
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead>Date</TableHead>
                                                    <TableHead>Time</TableHead>
                                                    <TableHead>Service</TableHead>
                                                    <TableHead>Status</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {filteredHistory.map((appt) => (
                                                    <TableRow key={appt.id}>
                                                        <TableCell>{formatDate(appt.date)}</TableCell>
                                                        <TableCell>
                                                            {appt.time ? appt.time.substring(0, 5) : '-'}
                                                        </TableCell>
                                                        <TableCell>
                                                            {appt.service_names || appt.service_name || appt.service?.name || '-'}
                                                        </TableCell>
                                                        <TableCell>
                                                            <span className={`capitalize px-2 py-1 rounded-full text-xs font-medium ${
                                                                appt.status === 'completed' ? 'bg-green-100 text-green-800' :
                                                                appt.status === 'confirmed' ? 'bg-blue-100 text-blue-800' :
                                                                appt.status === 'scheduled' ? 'bg-yellow-100 text-yellow-800' :
                                                                appt.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                                                                appt.status === 'no_show' ? 'bg-orange-100 text-orange-800' :
                                                                'bg-gray-100 text-gray-800'
                                                            }`}>
                                                                {appt.status?.replace('_', ' ')}
                                                            </span>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}